import { adsApi } from '@/api/client';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';

export type WorkspaceActionState = 'ENABLED' | 'PAUSED';

export type WorkspaceBulkActionOption = {
  id: string;
  label: string;
  disabled?: boolean;
  description?: string;
};

type CampaignStateTarget = Pick<SpCampaignNormalized, 'campaignId' | 'adProduct' | 'name'>;

type ProductAdStateTarget = {
  adId: string;
  label: string;
};

export async function applyWorkspaceCampaignStateChange(
  profileId: string,
  campaigns: CampaignStateTarget[],
  state: WorkspaceActionState
): Promise<{ successCount: number; failureCount: number; messages: string[] }> {
  let successCount = 0;
  let failureCount = 0;
  const messages: string[] = [];

  for (const campaign of campaigns) {
    try {
      const promise =
        campaign.adProduct === 'SP'
          ? adsApi.updateSpCampaignState(profileId, campaign.campaignId, { state })
          : campaign.adProduct === 'SB'
            ? adsApi.updateSbCampaignState(profileId, campaign.campaignId, { state })
            : adsApi.updateSdCampaignState(profileId, campaign.campaignId, { state });
      const res = await promise;
      if (!res.success) throw new Error('Request failed');
      successCount += 1;
    } catch (error) {
      failureCount += 1;
      const message = error instanceof Error ? error.message : 'Request failed';
      messages.push(`${campaign.name}: ${message}`);
    }
  }

  return { successCount, failureCount, messages };
}

export function summarizeWorkspaceCampaignStateChange(
  successCount: number,
  failureCount: number,
  state: WorkspaceActionState
): string {
  const verb = state === 'ENABLED' ? 'enabled' : 'paused';
  if (failureCount === 0) {
    return `${verb[0].toUpperCase()}${verb.slice(1)} ${successCount} campaign${successCount === 1 ? '' : 's'}`;
  }
  return `${verb[0].toUpperCase()}${verb.slice(1)} ${successCount} campaign${successCount === 1 ? '' : 's'}, ${failureCount} failed`;
}

type AdGroupStateTarget = {
  adGroupId: string;
  adProduct: CampaignAdProduct | string;
  name: string;
};

export async function applyWorkspaceAdGroupStateChange(
  profileId: string,
  adGroups: AdGroupStateTarget[],
  state: WorkspaceActionState
): Promise<{ successCount: number; failureCount: number; messages: string[] }> {
  let successCount = 0;
  let failureCount = 0;
  const messages: string[] = [];

  for (const adGroup of adGroups) {
    try {
      const promise =
        adGroup.adProduct === 'SP'
          ? adsApi.updateSpAdGroupState(profileId, adGroup.adGroupId, { state })
          : adGroup.adProduct === 'SB'
            ? adsApi.updateSbAdGroupState(profileId, adGroup.adGroupId, { state })
            : adsApi.updateSdAdGroupState(profileId, adGroup.adGroupId, { state });
      const res = await promise;
      if (!res.success) throw new Error('Request failed');
      successCount += 1;
    } catch (error) {
      failureCount += 1;
      const message = error instanceof Error ? error.message : 'Request failed';
      messages.push(`${adGroup.name}: ${message}`);
    }
  }

  return { successCount, failureCount, messages };
}

export function summarizeWorkspaceAdGroupStateChange(
  successCount: number,
  failureCount: number,
  state: WorkspaceActionState
): string {
  const verb = state === 'ENABLED' ? 'enabled' : 'paused';
  if (failureCount === 0) {
    return `${verb[0].toUpperCase()}${verb.slice(1)} ${successCount} ad group${successCount === 1 ? '' : 's'}`;
  }
  return `${verb[0].toUpperCase()}${verb.slice(1)} ${successCount} ad group${successCount === 1 ? '' : 's'}, ${failureCount} failed`;
}

export async function applyWorkspaceSpProductAdStateChange(
  profileId: string,
  productAds: ProductAdStateTarget[],
  state: WorkspaceActionState
): Promise<void> {
  if (!productAds.length) throw new Error('Select at least one supported product ad');
  const res = await adsApi.updateSpProductAdsState(profileId, {
    productAds: productAds.map((item) => ({ adId: item.adId, state })),
  });
  if (!res.success) throw new Error('Failed to update product ad state');

  const productAdsResult = (res.data as { productAds?: { error?: Array<{ description?: string }> } })
    ?.productAds;
  const errors = productAdsResult?.error ?? [];
  if (errors.length > 0) {
    throw new Error(errors[0]?.description || 'Amazon rejected one or more product ad updates');
  }
}

export function summarizeWorkspaceProductAdStateChange(
  updatedCount: number,
  skippedCount: number,
  state: WorkspaceActionState,
  entityLabel: string
): string {
  const verb = state === 'ENABLED' ? 'enabled' : 'paused';
  if (skippedCount === 0) {
    return `${verb[0].toUpperCase()}${verb.slice(1)} ${updatedCount} ${entityLabel}${updatedCount === 1 ? '' : 's'}`;
  }
  return `${verb[0].toUpperCase()}${verb.slice(1)} ${updatedCount} ${entityLabel}${updatedCount === 1 ? '' : 's'} (${skippedCount} unsupported skipped)`;
}

/** Actions omitted entirely from demo bulk menus. */
export const DEMO_EXCLUDED_ACTION_IDS = new Set([
  'remove_audience',
  'add_audience_sd',
  'add_tag',
  'edit_tags',
]);

/** Actions that remain disabled for demo (shown as Coming soon). */
export const DEMO_KEEP_DISABLED_ACTION_IDS = new Set(['bid_boost']);

/** For demo accounts, enable otherwise-disabled Coming soon actions (except excluded). */
export function withDemoActions(
  options: WorkspaceBulkActionOption[],
  isDemo: boolean,
  excludeIds: ReadonlySet<string> = DEMO_EXCLUDED_ACTION_IDS
): WorkspaceBulkActionOption[] {
  if (!isDemo) return options;
  return options
    .filter((opt) => !excludeIds.has(opt.id))
    .map(({ id, label, description }) =>
      DEMO_KEEP_DISABLED_ACTION_IDS.has(id)
        ? { id, label, disabled: true, description: description ?? 'Coming soon' }
        : { id, label }
    );
}

const CAMPAIGN_ACTION_OPTIONS: WorkspaceBulkActionOption[] = [
  { id: 'change_budget', label: 'Change Budget' },
  { id: 'smart_budget', label: 'Smart Budget' },
  { id: 'change_status', label: 'Change Status' },
  { id: 'change_placement', label: 'Change Placement' },
  { id: 'add_audience', label: 'Add Audience' },
  { id: 'bid_boost', label: 'Bid Boost', disabled: true, description: 'Coming soon' },
  { id: 'remove_audience', label: 'Remove Audience', disabled: true, description: 'Coming soon' },
  {
    id: 'add_audience_sd',
    label: 'Add Audience to SD campaigns',
    disabled: true,
    description: 'Coming soon',
  },
  { id: 'set_portfolio', label: 'Set Portfolio', disabled: true, description: 'Coming soon' },
  { id: 'add_tag', label: 'Add Tag', disabled: true, description: 'Coming soon' },
  { id: 'edit_tags', label: 'Edit Tags', disabled: true, description: 'Coming soon' },
];

export function supportedCampaignActionIds(isDemo = false): WorkspaceBulkActionOption[] {
  if (!isDemo) return CAMPAIGN_ACTION_OPTIONS;
  return withDemoActions(CAMPAIGN_ACTION_OPTIONS, true);
}

const KEY_LEVEL_ACTION_OPTIONS: WorkspaceBulkActionOption[] = [
  { id: 'add_negative', label: 'Add to Negative Keywords' },
  { id: 'add_to_campaign', label: 'Add to Campaign', disabled: true, description: 'Pending identifier mapping' },
  { id: 'add_tag', label: 'Add Tag', disabled: true, description: 'Coming soon' },
  { id: 'edit_tags', label: 'Edit Tags', disabled: true, description: 'Coming soon' },
];

/** Search terms: demo unlocks add-to-campaign; tags stay omitted. */
export function keyLevelActionOptions(isDemo = false): WorkspaceBulkActionOption[] {
  if (!isDemo) {
    return KEY_LEVEL_ACTION_OPTIONS.filter(
      (opt) => opt.id === 'add_negative' || Boolean(opt.disabled)
    );
  }
  return withDemoActions(KEY_LEVEL_ACTION_OPTIONS, true);
}

/** Live keywords: Change Bid and Set to suggested bid. Demo also unlocks status (not tags). */
export function keywordLevelActionOptions(isDemo = false): WorkspaceBulkActionOption[] {
  const options: WorkspaceBulkActionOption[] = [
    { id: 'change_bid', label: 'Change Bid' },
    { id: 'set_suggested_bid', label: 'Set to suggested bid' },
  ];
  if (!isDemo) return options;
  return [...options, { id: 'change_status', label: 'Change Status' }];
}

export function productStatusActionOptions() {
  return [{ id: 'change_status', label: 'Change Status' }];
}

export function adGroupLevelActionOptions() {
  return [{ id: 'change_status', label: 'Change Status' }];
}

export function isSupportedProductAd(adProduct: CampaignAdProduct | 'SP' | 'SD' | string): boolean {
  return adProduct === 'SP';
}

/** Demo-only campaign action ids that open mock modals (not real API flows). */
export const DEMO_CAMPAIGN_MOCK_ACTION_IDS = new Set(['set_portfolio']);

export type DemoNegativeTarget = {
  text: string;
  campaignId: string;
  campaignName: string;
  adGroupId: string;
  adGroupName: string;
};

export type DemoNegativeGroup = {
  adGroupId: string;
  adGroupName: string;
  campaignId: string;
  campaignName: string;
  terms: string[];
};

export function groupDemoNegativesByAdGroup(targets: DemoNegativeTarget[]): DemoNegativeGroup[] {
  const byAdGroup = new Map<string, DemoNegativeGroup>();
  for (const target of targets) {
    const key = target.adGroupId || `${target.campaignId}|unknown`;
    const existing = byAdGroup.get(key);
    if (existing) {
      if (!existing.terms.includes(target.text)) existing.terms.push(target.text);
      continue;
    }
    byAdGroup.set(key, {
      adGroupId: target.adGroupId,
      adGroupName: target.adGroupName || target.adGroupId || '—',
      campaignId: target.campaignId,
      campaignName: target.campaignName || target.campaignId || '—',
      terms: [target.text],
    });
  }
  return [...byAdGroup.values()].sort((a, b) => {
    const campaignCmp = a.campaignName.localeCompare(b.campaignName);
    if (campaignCmp !== 0) return campaignCmp;
    return a.adGroupName.localeCompare(b.adGroupName);
  });
}
